import React, { useEffect, useRef, useState } from 'react';
import { popularCities } from '../onboarding/cities';
import { City } from '../onboarding/onboarding.type';
import styles from './city-input.module.css';

type CityInputProps = {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  label?: string;
  error?: string | boolean | undefined;
  isDisabled?: boolean;
  className?: string;
};

const CityInput = (props: CityInputProps) => {
  const {
    value,
    error,
    onChange,
    placeholder = 'Update City',
    label = '',
    isDisabled = false,
    className,
  } = props;
  const [suggestions, setSuggestions] = useState<City[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [activeSuggestionIndex, setActiveSuggestionIndex] = useState(-1);

  const inputRef = useRef<HTMLInputElement>(null);
  const blurTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const suggestionsRef = useRef<HTMLDivElement>(null);

  const searchCities = (inputValue: string): City[] => {
    if (inputValue.length < 2) return [];

    return popularCities
      .filter(city =>
        city.n.toLowerCase().includes(inputValue.toLowerCase()),
      )
      .slice(0, 8);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value;
    onChange(inputValue);

    // Check if the input value exactly matches a city (likely browser autofill)
    const exactMatch = popularCities.find(city =>
      city.n.toLowerCase() === inputValue.toLowerCase() ||
      `${city.n}, ${city.c}`.toLowerCase() === inputValue.toLowerCase(),
    );

    if (exactMatch) {
      // If exact match found, hide suggestions (browser autofill was likely used)
      setSuggestions([]);
      setShowSuggestions(false);
      setActiveSuggestionIndex(-1);
      return;
    }

    if (inputValue.length >= 2) {
      const results = searchCities(inputValue);
      setSuggestions(results);
      setShowSuggestions(true);
      setActiveSuggestionIndex(-1);
    } else {
      setSuggestions([]);
      setShowSuggestions(false);
    }
  };

  const handleSuggestionClick = (city: City) => {
    onChange(city.n);
    setShowSuggestions(false);
    setSuggestions([]);
    setActiveSuggestionIndex(-1);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!showSuggestions || suggestions.length === 0) return;

    switch (e.key) {
    case 'ArrowDown':
      e.preventDefault();
      setActiveSuggestionIndex(prev =>
        prev < suggestions.length - 1 ? prev + 1 : prev,
      );
      break;

    case 'ArrowUp':
      e.preventDefault();
      setActiveSuggestionIndex(prev => prev > 0 ? prev - 1 : -1);
      break;

    case 'Enter':
      e.preventDefault();
      e.stopPropagation();
      if (activeSuggestionIndex >= 0 && activeSuggestionIndex < suggestions.length) {
        handleSuggestionClick(suggestions[activeSuggestionIndex]);
      } else {
        setShowSuggestions(false);
        setActiveSuggestionIndex(-1);
      }
      break;

    case 'Escape':
      setShowSuggestions(false);
      setActiveSuggestionIndex(-1);
      break;
    }
  };

  const handleBlur = () => {
    // Clear any existing timeout
    if (blurTimeoutRef.current) {
      clearTimeout(blurTimeoutRef.current);
    }

    // Delay hiding suggestions to allow click events on suggestions
    blurTimeoutRef.current = setTimeout(() => {
      setShowSuggestions(false);
      setActiveSuggestionIndex(-1);
      blurTimeoutRef.current = null;
    }, 150);
  };

  const handleFocus = () => {
    if (value.length >= 2 && suggestions.length > 0) {
      setShowSuggestions(true);
    }
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        suggestionsRef.current &&
        !suggestionsRef.current.contains(event.target as Node) &&
        inputRef.current &&
        !inputRef.current.contains(event.target as Node)
      ) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    return () => {
      if (blurTimeoutRef.current) {
        clearTimeout(blurTimeoutRef.current);
      }
    };
  }, []);

  return (
    <div className={styles.container}>
      <label htmlFor="city" className={styles.label}>
        {label}
      </label>
      <input
        id={'city'}
        ref={inputRef}
        type="text"
        value={value}
        onChange={handleInputChange}
        onKeyDown={handleKeyDown}
        onBlur={handleBlur}
        onFocus={handleFocus}
        placeholder={placeholder}
        disabled={isDisabled}
        className={`${styles.input} ${className}`}
        autoComplete="off"
      />
      {error && <div className={styles.error}>{error}</div>}

      {showSuggestions && suggestions.length > 0 && (
        <div ref={suggestionsRef} className={styles.suggestions}>
          {suggestions.map((city, index) => (
            <div
              key={`${city.n}-${city.c}`}
              onClick={() => handleSuggestionClick(city)}
              className={`${styles.suggestion} ${index === activeSuggestionIndex ? styles.suggestionActive : ''}`}
              onMouseEnter={() => setActiveSuggestionIndex(index)}
            >
              <span className={styles.cityName}>{city.n}</span>
              <span className={styles.countryName}>{city.c}</span>
            </div>
          ))}

          {value.length >= 2 && !suggestions.find(city =>
            city.n.toLowerCase() === value.toLowerCase() ||
            `${city.n}, ${city.c}`.toLowerCase() === value.toLowerCase(),
          )}
        </div>
      )}
    </div>
  );
};

export default CityInput;
